import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axiosInstance from '../api/axiosInstance'; // Import the configured Axios instance

// Determine API base URL (ensure consistency, maybe move to a config file later)
const API_BASE = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_BASE || 'http://localhost:5002');

// 1. Create the Context
const AuthContext = createContext(null);

// 2. Create the Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start loading until first check is done
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Fetch user info (moved logic from useAuth hook)
  const fetchUser = useCallback(async () => {
    // Don't reset loading to true here if called manually later,
    // only the initial load should show the main loading state.
    // setLoading(true);
    setError(null);
    try {
      // Using axiosInstance which is pre-configured with base URL and credentials
      const response = await axiosInstance.get('/api/user');
      if (response.status === 200 && response.data.user) {
        setUser(response.data.user);
      } else {
        // This case might be hit if the backend returns 200 but no user (e.g., session expired)
        setUser(null);
      }
    } catch (err) {
      // If the error is due to unauthorized access (e.g., 401), it's an expected state for a logged-out user.
      // In this case, we silently handle it by setting the user to null without setting an error.
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        setUser(null); // User is not authenticated, which is fine.
      } else {
        // For other errors (like network issues or server errors), we should show an error message.
        console.error("Auth fetch error:", err.response ? err.response.data : err.message);
        setError('Failed to fetch user status'); // Set error for unexpected issues.
        setUser(null);
      }
    } finally {
      // Only set loading to false after the *initial* fetch
      if (loading) {
          setLoading(false);
      }
    }
  }, [loading]); // Depend on loading to ensure setLoading(false) runs correctly on initial load

  // Initial fetch on component mount
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUser]); // fetchUser is memoized, so this runs once on mount

  // Login redirect (moved logic from useAuth hook)
  const login = useCallback((provider) => {
    const returnPath = encodeURIComponent(window.location.pathname);
    let authUrl;

    switch (provider) {
      case 'apple':
        authUrl = `${API_BASE}/auth/apple?return_to=${returnPath}`;
        break;
      case 'facebook':
        authUrl = `${API_BASE}/auth/facebook?return_to=${returnPath}`;
        break;
      case 'google':
      default:
        authUrl = `${API_BASE}/auth/google?return_to=${returnPath}`;
        break;
    }

    if (authUrl) {
      window.location.href = authUrl;
    }
  }, []);

  // Logout (moved logic from useAuth hook)
  const logout = useCallback(async () => {
    // Indicate loading state during logout process
    // setLoading(true); // Optional: could show a spinner during logout API call
    setError(null);
    try {
      // Use axiosInstance for consistency. The backend route is simple, so a GET request is fine.
      const response = await axiosInstance.get('/auth/logout');
      if (response.status === 200) {
        setUser(null);
        setNotification("Signed out successfully.");
      } else {
        // Axios error handling is different, non-2xx responses are caught below
        setError('Logout failed. Please try again.');
      }
    } catch (err) {
      console.error("Logout error:", err.response ? err.response.data : err.message);
      setError('Logout failed due to a server or network issue.');
    } finally {
       // setLoading(false); // Optional: stop spinner if one was shown
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setError(null);
    try {
      // Use axiosInstance for the DELETE request to ensure the API key is included
      const res = await axiosInstance.delete('/api/user');
      
      if (res.status === 200) {
        // The backend handles logout, so we just need to update the local state
        setUser(null);
        return { success: true, message: 'Account deletion initiated.' };
      } else {
        // Axios wraps errors, so non-2xx responses will be caught in the catch block.
        // This part might be redundant but kept for safety.
        const errorMsg = res.data?.error || 'Failed to delete account.';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      }
    } catch (err) {
      console.error("Delete account network error:", err);
      const errorMsg = err.response?.data?.error || 'Delete account failed due to a network or server issue.';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }
  }, []);

  // Value provided to consuming components
  const value = {
    user,
    loading,
    error,
    notification,
    setNotification,
    login,
    logout,
    fetchUser, // Expose fetchUser in case manual refresh is needed elsewhere
    deleteAccount,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 3. Custom Hook to Consume Context (optional but good practice)
// This replaces the old useAuth hook's implementation
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
   // If context is null initially before provider sets value, handle it gracefully
   // (though with the default value set to null in createContext, this might not be strictly needed)
   if (context === null) {
       console.warn("AuthContext is null, ensure AuthProvider wraps the component tree.");
       // Return a default shape or handle as appropriate
       return { user: null, loading: true, error: null, login: () => {}, logout: () => {}, fetchUser: async () => {} };
   }
  return context;
};